import React from 'react';
import { merge } from 'lodash';
import { UX2, constants } from '@wsb/guac-widget-core';
import Default from '../../../common/Themes/Default/Theme';
import { multiLinePadded } from '../../../common/utils/text';
import { sectionHrTypes } from '../../../common/constants';
import * as utils from '../../../common/Themes/Default/Dials/Colors/utils';
import { getMenuHeadingStyle } from '../../../common/utils/legacyOverrides';
import { minimalIconMapping } from '../../../common/Components/Internal/Header/utils/mapPropsToSocialProps';
import * as filledIcons from '../../../common/IconPacks/humanisticFilled';
import * as socialIconPack from '../../../common/IconPacks/SocialIcons/minimalSocialIconPack';
import { mergeTypographyOverrides } from '../../../common/utils/typography';
import { levelFilter } from '../../../common/utils/text';
import { fontSizeByDevice } from '../../../common/Themes/Default/Dials/Fonts/utils/getSizesByDevice';
import themeConfig from '../themeConfig';

const { NONE } = sectionHrTypes;
const { buttons } = constants;

const alphaLetterSpacing = '5px';
const betaLetterSpacing = '6px';

class Theme22 extends Default {
  static config = themeConfig;

  static get displayName() {
    return 'Theme22';
  }

  static excludedProps = Default.excludedProps.concat('phone');

  static getMutatorDefaultProps(widgetType, widgetPreset) {
    const defaultProps = super.getMutatorDefaultProps(widgetType, widgetPreset);

    return widgetType === 'HEADER'
      ? {
        ...defaultProps,
        usePhone: false,
        useCropBackground: true,
        useSocialLinks: true,
        hasLogoBackground: true,
        hasLogoAlign: true,
        headerTreatmentsConfig: {
          ...defaultProps.headerTreatmentsConfig,
          ...themeConfig.headerTreatmentsConfig
        }
      }
      : defaultProps;
  }

  static getWidgetDefaultProps(widgetType, widgetPreset) {
    return {
      ...super.getWidgetDefaultProps(widgetType, widgetPreset),
      ...(widgetType === 'HEADER' && { alignmentOption: 'left' })
    };
  }

  constructor() {
    super();
    this.mappedValues = {
      ...this.mappedValues,
      backgroundColorInput() {
        const [background] = utils.getDial(this, ['background']);
        return background.setAlpha(100).setLightness(95);
      },
      borderColorPrimary() {
        const [primary, background, multiplier] = utils.getDial(this, [
          'primary',
          'background',
          'multiplier'
        ]);
        return primary.contrastWith(background, 10 * multiplier, true);
      },
      fontSizeMapping: {
        ...fontSizeByDevice,
        xs: {
          xsmall: 12,
          small: 14,
          medium: 16,
          mlarge: 18,
          large: 20,
          xlarge: 24,
          xxlarge: 28,
          xxxlarge: 32,
          jumbo: 40,
          xjumbo: 44,
          xxjumbo: 48
        }
      },
      typographyOverrides: {
        HeadingAlpha: {
          style: {
            font: 'alternate',
            color: 'highContrast',
            fontSize: 'xxxlarge',
            fontWeight: 'normal',
            letterSpacing: alphaLetterSpacing,
            textTransform: 'uppercase',
            lineHeight: 1.35
          }
        },
        HeadingBeta: {
          style: {
            font: 'alternate',
            color: 'highContrast',
            fontSize: 'medium',
            fontWeight: 'normal',
            textTransform: 'uppercase',
            letterSpacing: betaLetterSpacing,
            lineHeight: '2'
          }
        },
        HeadingEpsilon: {
          style: {
            font: 'primary',
            color: 'highContrast',
            fontSize: 'xxlarge',
            fontWeight: 'normal',
            letterSpacing: 'normal',
            textTransform: 'none'
          }
        },
        HeadingDelta: {
          style: {
            font: 'alternate',
            color: 'highContrast',
            fontSize: 'large',
            fontWeight: 'normal',
            letterSpacing: 'normal',
            textTransform: 'none'
          }
        },
        NavAlpha: {
          style: {
            font: 'alternate',
            color: 'highContrast',
            fontSize: 'small',
            fontWeight: 'normal',
            letterSpacing: '0.143em',
            textTransform: 'uppercase',
            textDecoration: 'none'
          },
          active: {
            style: {
              color: 'highContrast',
              fontWeight: 'normal'
            }
          }
        },
        SubNavAlpha: {
          style: {
            font: 'alternate',
            color: 'section',
            fontSize: 'medium',
            fontWeight: 'normal',
            letterSpacing: 'normal',
            textTransform: 'none'
          }
        },
        NavBeta: {
          style: {
            font: 'alternate',
            color: 'highContrast',
            fontSize: 'large',
            fontWeight: 'normal',
            letterSpacing: '2px',
            textTransform: 'uppercase'
          }
        },
        ButtonAlpha: {
          style: {
            font: 'alternate',
            fontWeight: 'bold',
            letterSpacing: '0.143em',
            textTransform: 'uppercase',
            textDecoration: 'none'
          }
        },
        SubNavBeta: {
          style: {
            font: 'alternate',
            color: 'section',
            fontSize: 'medium',
            fontWeight: 'normal',
            letterSpacing: 'normal',
            textTransform: 'none'
          }
        },
        InputAlpha: props => {
          return merge(mergeTypographyOverrides.call(this, 'BodyAlpha', props), {
            style: {
              color: 'input',
              ['@xs-only']: {
                fontSize: 'medium' // 16px
              }
            }
          });
        }
      }
    };
  }

  headingBorderStyles = {
    borderBottomWidth: 'xsmall',
    borderBottomStyle: 'solid',
    borderColor: 'inputDisabled',
    paddingBottom: 'small'
  };

  genSpanPaddingStyles() {
    return {
      paddingVertical: 'xsmall',
      paddingHorizontal: 'small'
    };
  }

  /* Basic elements */
  Heading(props) {
    const { tag, level } = props;
    const { widgetType, widgetPreset } = this.base;
    const menuOverrideStyle = getMenuHeadingStyle(tag, widgetType, widgetPreset);
    const currentLevel = levelFilter({ tag, level });
    const typographyMapping = {
      2: 'HeadingEpsilon'
    };

    if (currentLevel === 5) {
      return super.Heading(this.merge({ style: menuOverrideStyle }, props));
    }

    return super.Heading(
      this.merge(
        {
          typography: typographyMapping[currentLevel]
        },
        props
      )
    );
  }

  HeadingMajor(props) {
    return super.HeadingMajor(
      this.merge(
        {
          typography: 'HeadingGamma'
        },
        props
      )
    );
  }

  HeadingMinor({ border = true, ...props }) {
    const borderStyle = border ? this.headingBorderStyles : {};
    return super.HeadingMinor(
      this.merge(
        {
          style: {
            ...borderStyle
          },
          border
        },
        props
      )
    );
  }

  HeadingProduct(props) {
    return this.HeadingMinor(
      this.merge(
        {
          border: false
        },
        props
      )
    );
  }

  Button(props) {
    const { fill = this.getButtonDefaults().fill } = props;
    const animation =
      fill !== buttons.fills.OPEN
        ? {
          style: {
            transitionProperty: 'background-color',
            transitionDuration: 'medium',
            transitionTimingFunction: 'ease-in-out'
          }
        }
        : {};
    return super.Button(
      this.merge(
        {
          style: {
            [' img']: {
              boxShadow: 'none'
            }
          }
        },
        animation,
        props
      )
    );
  }

  sharedInputStyles(section) {
    return {
      style: {
        borderColor: 'input',
        borderWidth: section === 'overlay' ? 'xsmall' : '0',
        borderRadius: 'none',
        borderStyle: 'solid'
      }
    };
  }

  Input(props) {
    const { section } = this.base;
    return super.Input(
      this.merge(
        { ...this.sharedInputStyles(section) },
        {
          style: {
            paddingVertical: 'small',
            paddingHorizontal: 'small'
          }
        },
        props
      )
    );
  }

  InputFloatLabelInput(props) {
    return super.InputFloatLabelInput(
      this.merge(
        {
          style: {
            paddingBottom: 'xsmall'
          }
        },
        props
      )
    );
  }

  InputFloatLabelLabel(props) {
    return super.InputFloatLabelLabel(
      this.merge(
        {
          style: {
            left: '16px'
          }
        },
        props
      )
    );
  }

  InputSelect(props) {
    const { section } = this.base;
    return super.InputSelect(
      this.merge(
        {
          ...this.sharedInputStyles(section)
        },
        props
      )
    );
  }

  InputSelectElement(props) {
    return super.InputSelectElement(
      this.merge(
        {
          style: {
            paddingVertical: 'small',
            paddingHorizontal: 'small'
          }
        },
        props
      )
    );
  }

  Dropdown(props) {
    return super.Dropdown(
      this.merge(
        {
          style: {
            borderRadius: 0
          }
        },
        props
      )
    );
  }

  Icon({ icon, ...props }) {
    icon = minimalIconMapping[icon] || icon;
    return super.Icon(
      this.merge(
        {
          icon,
          iconPack: { ...filledIcons, ...socialIconPack }
        },
        props
      )
    );
  }

  ContentCardWrapperImage(props) {
    const style =
      this.base.widgetPreset === 'content2'
        ? {
          borderRadius: 0
        }
        : {};
    return super.ContentCardWrapperImage(this.merge({ style }, props));
  }

  Image(props) {
    const plainImageWidgets = ['content2', 'socialFeed1'];
    const style =
      plainImageWidgets.indexOf(this.base.widgetPreset) === -1
        ? {
          boxShadow: '0 2px 6px 0px rgba(0,0,0,0.2)',
          borderRadius: 0
        }
        : {};
    return super.Image(this.merge({ style }, props));
  }

  ImageLogo(props) {
    return super.ImageLogo(
      this.merge(
        {
          style: {
            boxShadow: 'none'
          }
        },
        props
      )
    );
  }

  ImageThumbnail(props) {
    return super.ImageThumbnail(
      this.merge(
        {
          style: {
            boxShadow: '0 2px 6px 0px rgba(0,0,0,0.2)',
            borderRadius: 0
          }
        },
        props
      )
    );
  }

  /* Nav */

  NavLink(props) {
    return super.NavLink(
      this.merge(
        {
          style: {
            paddingHorizontal: 0
          }
        },
        props
      )
    );
  }

  NavLinkActive(props) {
    const paddingOffset = this.parseSizedUnit(this.mapPropValue('padding', '-xxsmall'))[0];
    const borderOffset = this.parseSizedUnit(this.mapPropValue('border', '-small'))[0];
    const marginBottom = paddingOffset + borderOffset;
    return this.NavLink(
      this.merge(
        {
          style: {
            borderTopWidth: 0,
            borderRightWidth: 0,
            borderLeftWidth: 0,
            borderBottomWidth: 'small',
            borderStyle: 'solid',
            borderColor: 'primary',
            paddingBottom: 'xxsmall',
            marginBottom
          },
          active: true
        },
        props
      )
    );
  }

  NavFooterLink(props) {
    return this.ContentLink(
      this.merge(
        {
          style: {
            'paddingVertical': '5px !important',
            'paddingHorizontal': '0px !important',
            'marginHorizontal': 'xsmall',
            'display': 'inline-block',
            '@xs-only': {
              border: 0
            },
            '@sm-only': {
              border: 0
            }
          }
        },
        props
      )
    );
  }

  NavMenuLinkActive(props) {
    return this.NavMenuLink(
      this.merge(
        {
          style: {
            border: 'none'
          },
          active: true
        },
        props
      )
    );
  }

  ListNested(props) {
    return super.List(
      this.merge(
        {
          style: {
            '*': {
              textTransform: 'none',
              color: 'lowContrast'
            }
          }
        },
        props
      )
    );
  }

  Pipe(props) {
    return super.Pipe(
      this.merge(
        {
          style: {
            backgroundColor: 'action'
          }
        },
        props
      )
    );
  }

  UtilitiesMenuLink(props) {
    return super.UtilitiesMenuLink(
      this.merge(
        {
          style: {
            verticalAlign: 'middle'
          }
        },
        props
      )
    );
  }

  FormSearchInputSearch(props) {
    return this.InputSearch(
      this.merge(
        {
          style: {
            borderColor: 'lowContrast',
            backgroundColor: 'white'
          }
        },
        props
      )
    );
  }

  Table(props) {
    return super.Table(
      this.merge(
        {
          style: {
            borderRadius: 0
          }
        },
        props
      )
    );
  }

  /* Header */

  Hero(props) {
    return super.Hero(
      this.merge(
        {
          style: {
            'paddingVertical': 0,
            'textAlign': 'center',
            'marginHorizontal': 0,
            'marginTop': 'medium',
            'marginBottom': 'xsmall',
            'display': 'flex',
            'flexDirection': 'column',
            'alignItems': 'center',
            'width': '100%',
            '@md': {
              marginTop: 'small',
              marginBottom: 0,
              width: '50vw'
            }
          }
        },
        props
      )
    );
  }

  HeroRight(props) {
    return this.Hero(
      this.merge(
        {
          style: {
            alignItems: 'flex-end'
          }
        },
        props
      )
    );
  }

  HeroLeft(props) {
    return this.Hero(
      this.merge(
        {
          style: {
            alignItems: 'flex-start'
          }
        },
        props
      )
    );
  }

  Tagline(props) {
    const styleOverrides = {
      backgroundColor: 'white',
      color: 'black',
      ...this.genSpanPaddingStyles()
    };
    const { renderMode } = this.base;
    return super.Tagline(
      this.merge(
        multiLinePadded({ styleOverrides, renderMode }),
        {
          category: 'neutral',
          section: 'default',
          wrapWithTag: 'span',
          style: {
            marginBottom: '0 !important',
            maxWidth: '100%'
          }
        },
        props
      )
    );
  }

  HeroCenterHeading(props) {
    return this.HeroHeading(
      this.merge(
        {
          style: {
            borderBottomStyle: 'solid',
            borderBottomWidth: '1px',
            borderColor: 'rgba(255, 255, 255, 0.5)',
            paddingBottom: 'small'
          }
        },
        props
      )
    );
  }

  SubTagline(props) {
    const { renderMode } = this.base;
    const styleOverrides = {
      backgroundColor: 'black',
      color: 'white',
      lineHeight: '2',
      paddingHorizontal: 'small',
      paddingVertical: '0.5em'
    };
    const listStyles = {
      backgroundColor: 'black',
      color: 'white',
      width: 'auto',
      display: 'table',
      marginBottom: 0
    };
    const otherStyleOverrides = {
      ul: listStyles,
      ol: listStyles
    };
    const altFontFamily = this.mapPropValue('fontFamily', 'alternate');
    const marginStyle =
      altFontFamily.indexOf('Open Sans') > -1
        ? {
          marginBottom: renderMode === 'EDIT' ? '0.25em' : '0.4em'
        }
        : {
          marginBottom: '0.5em',
          ['@xl']: {
            marginBottom: renderMode === 'EDIT' ? 0 : renderMode === 'PUBLISH' ? '0.1em' : '0.2em'
          }
        };
    return super.Text(
      this.merge(
        multiLinePadded({ styleOverrides, otherStyleOverrides, renderMode }),
        {
          style: {
            maxWidth: '80%',
            ...marginStyle
          }
        },
        props
      )
    );
  }

  HeroButtonPrimary(props) {
    return this.ButtonPrimary(
      this.merge(
        {
          style: {
            'width': 'auto',
            '@md': {
              maxWidth: 500
            }
          }
        },
        props
      )
    );
  }

  HeroLink(props) {
    return this.Link(props);
  }

  /* Section */

  Section({ children, skipDivider, ...props }) {
    const style = {
      paddingBottom: 'xlarge',
      paddingTop:
        this.base.widgetType === 'FOOTER'
          ? this.base.widgetPreset === 'footer4'
            ? 'xlarge'
            : 0
          : 'xlarge'
    };
    if (this.base.widgetPreset === 'content5') {
      style.backgroundColor = 'transparent';
    }
    let updatedProps = this.merge({ style, children }, props);
    if (this.base.widgetType === 'FOOTER' && !skipDivider) {
      updatedProps = this.merge(
        {
          style,
          children: (
            <React.Fragment>
              <UX2.Element.Divider style={{ paddingVertical: 'medium' }} />
              { children }
            </React.Fragment>
          )
        },
        props
      );
    }
    return super.Section(updatedProps);
  }

  SectionHeading({ children, richtext, ...props }) {
    const colorTest = this.mapPropValue('backgroundColor', 'sectionContrast');
    const styleOverrides = {
      backgroundColor: colorTest.lightness > 30 ? 'white' : 'black',
      color: colorTest.lightness > 30 ? 'black' : 'white',
      display: 'inline-block',
      lineHeight: '1',
      ...this.genSpanPaddingStyles()
    };

    const { renderMode } = this.base;
    return super.SectionHeading(
      this.merge(
        multiLinePadded({ styleOverrides, renderMode }),
        {
          style: {
            textAlign: 'center'
          },
          children: (
            <UX2.Element.Element richtext={ richtext } nakedElement={ true } children={ children } />
          ),
          sectionHeadingHR: NONE,
          typography: 'HeadingBeta'
        },
        props
      )
    );
  }

  SectionBannerHeading(props) {
    return this.SectionHeading(props);
  }

  Intro(props) {
    return super.Intro(this.merge({ alignment: 'center' }, props));
  }

  /* Mobile */

  NavigationDrawer(props) {
    return super.NavigationDrawer(
      this.merge(
        {
          category: 'neutral',
          section: 'default',
          style: {
            backgroundColor: 'section',
            width: '80%'
          }
        },
        props
      )
    );
  }

  NavigationDrawerInputSearch(props) {
    return super.NavigationDrawerInputSearch(
      this.merge(
        {
          style: {
            backgroundColor: 'searchMobileLight',
            borderColor: 'lowContrast'
          }
        },
        props
      )
    );
  }

  NavigationDrawerLink(props) {
    return super.NavigationDrawerLink(
      this.merge(
        {
          style: {
            justifyContent: 'flex-start',
            minWidth: '60vw'
          }
        },
        props
      )
    );
  }

  NavigationDrawerLinkActive({ children, ...props }) {
    return super.NavigationDrawerLinkActive(
      this.merge(
        {
          style: {
            '> span': {
              borderTopWidth: 0,
              borderLeftWidth: 0,
              borderRightWidth: 0,
              borderBottomWidth: 'small',
              borderStyle: 'solid',
              borderColor: 'primary',
              paddingBottom: 'xsmall'
            }
          },
          children: <span>{ children }</span>
        },
        props
      )
    );
  }

  NavigationDrawerCloseIcon(props) {
    return super.CloseIcon(
      this.merge(
        {
          style: {
            display: 'none'
          }
        },
        props
      )
    );
  }

  NavigationDrawerListItem(props) {
    return super.NavigationDrawerListItem(
      this.merge(
        {
          style: {
            borderBottomWidth: 0
          }
        },
        props
      )
    );
  }

  /* Widgets */

  FeaturedHeading(props) {
    return super.FeaturedHeading(
      this.merge(
        {
          style: {
            ...this.headingBorderStyles,
            paddingBottom: 'medium',
            marginBottom: 'xlarge'
          },
          typography: 'HeadingGamma'
        },
        props
      )
    );
  }

  ContentLink(props) {
    return this.Link(
      this.merge(
        {
          style: {
            borderTopWidth: 0,
            borderLeftWidth: 0,
            borderRightWidth: 0,
            borderBottomWidth: 'small',
            borderStyle: 'solid',
            borderColor: 'primary'
          }
        },
        props
      )
    );
  }

  ContentCardImageThumbnail(props) {
    return this.ImageThumbnail(
      this.merge(
        {
          style: {
            width: '100%',
            height: '100%'
          }
        },
        props
      )
    );
  }

  ContentImageThumbnail(props) {
    return this.ImageThumbnail(
      this.merge(
        {
          style: {
            '@md': {
              width: '100%'
            }
          }
        },
        props
      )
    );
  }

  CardBannerContainer(props) {
    return super.CardBannerContainer(
      this.merge(
        {
          style: {
            '@md': {
              'justifyContent': 'center',
              'alignItems': 'center',
              'flexDirection': 'column',
              '> div:nth-child(2)': {
                paddingTop: 'xsmall'
              }
            }
          }
        },
        props
      )
    );
  }

  CardBannerBlock(props) {
    return super.CardBannerBlock(
      this.merge(
        {
          style: {
            'flexGrow': 0,
            'flexShrink': 0,
            '@md': {
              paddingHorizontal: '0px !important'
            }
          }
        },
        props
      )
    );
  }

  CardBannerHeading(props) {
    return super.HeadingMiddle(
      this.merge(
        {
          style: {
            marginBottom: 'small',
            textAlign: 'center'
          }
        },
        props
      )
    );
  }

  ContainerSplit(props) {
    if (this.base.widgetType !== 'ABOUT' && this.base.widgetType !== 'PAYMENT') {
      return super.ContainerSplit(props);
    }
    return super.ContainerSplit(
      this.merge(
        {
          style: {
            '@md': {
              display: 'block',
              position: 'relative'
            }
          }
        },
        props
      )
    );
  }

  CardHeading(props) {
    const { widgetPreset } = this.base;

    return super.CardHeading(
      this.merge(
        {
          ...(widgetPreset === 'rss1' && {
            typography: 'BodyAlpha',
            featured: true
          }),
          border: false
        },
        props
      )
    );
  }

  SplitItem(props) {
    if (this.base.widgetType !== 'ABOUT' && this.base.widgetType !== 'PAYMENT') {
      return super.SplitItem(props);
    }
    return super.SplitItem(
      this.merge(
        {
          style: {
            '@md': {
              ':not(:only-child)': {
                display: 'inline-block',
                zIndex: 1,
                backgroundColor: 'section',
                padding: 'medium',
                marginVertical: 'xlarge',
                marginHorizontal: 'xxxlarge',
                boxShadow: '0px 2px 6px rgba(0, 0, 0, .2)',
                width: '43%'
              },
              '> *': {
                maxWidth: '100%'
              }
            }
          }
        },
        props
      )
    );
  }

  SplitItemImage(props) {
    if (this.base.widgetType !== 'ABOUT' && this.base.widgetType !== 'PAYMENT') {
      return super.SplitItemImage(props);
    }
    const imageOverrideStyles = {
      marginVertical: 0,
      marginHorizontal: 0,
      display: 'flex',
      padding: 0,
      position: 'absolute',
      left: 0,
      right: 0,
      top: 0,
      bottom: 0,
      zIndex: 0,
      boxShadow: 'none',
      width: '100%',
      maxWidth: '100%'
    };
    return super.SplitItemImage(
      this.merge(
        {
          style: {
            '@md': {
              ...imageOverrideStyles,
              ':not(:only-child)': {
                ...imageOverrideStyles
              }
            }
          }
        },
        props
      )
    );
  }

  CarouselButtonNext(props) {
    return super.CarouselButtonNext(
      this.merge(
        {
          category: 'neutral',
          section: 'default',
          backgroundColor: 'sectionOverlay'
        },
        props
      )
    );
  }

  CarouselButtonPrevious(props) {
    return super.CarouselButtonPrevious(
      this.merge(
        {
          category: 'neutral',
          section: 'default',
          backgroundColor: 'sectionOverlay'
        },
        props
      )
    );
  }

  CarouselFigCaptionOverlay(props) {
    return super.CarouselFigCaptionOverlay(
      this.merge(
        {
          category: 'neutral',
          section: 'default',
          style: {
            'justifyContent': 'center',
            'paddingHorizontal': 'xxsmall',
            '> *': {
              padding: 'xxsmall',
              backgroundColor: 'sectionOverlay'
            },
            '> :first-child': {
              flexGrow: 0,
              paddingLeft: 'xsmall'
            },
            '> :last-child': {
              paddingRight: 'xsmall'
            }
          },
          typography: 'BodyAlpha'
        },
        props
      )
    );
  }

  MediaObjectBackground(props) {
    return super.MediaObjectBackground(
      this.merge(
        {
          style: {
            borderRadius: '0'
          }
        },
        props
      )
    );
  }

  EmbedVideo({ isVerticalVideo, ...props }) {
    if (isVerticalVideo) {
      return super.EmbedVideo(this.merge({ style: { maxHeight: '680px' } }, props));
    }
    return super.EmbedVideo(props);
  }

  WrapperInsetEmbedVideo({ isVerticalVideo, ...props }) {
    if (isVerticalVideo) {
      return super.WrapperInsetEmbedVideo(this.merge({ style: { maxHeight: '680px' } }, props));
    }
    return super.WrapperInsetEmbedVideo(props);
  }
}

export default Theme22;
